import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { fetchCalendarEvents, ensureToken, startAutoRefresh, getStoredToken } from '../lib/google';

const CALENDARS_TO_SYNC = [
  { id: 'primary', name: 'Personal', projectId: null },
];

export function useGoogle() {
  const [googleConnected, setGoogleConnected] = useState(!!getStoredToken());
  const [syncing, setSyncing] = useState(false);

  // On mount: if token exists, start auto-refresh
  useEffect(() => {
    if (getStoredToken()) {
      startAutoRefresh();
      setGoogleConnected(true);
    }
  }, []);

  // Listen for token updates (from silent refresh)
  useEffect(() => {
    const check = setInterval(() => {
      const has = !!getStoredToken();
      setGoogleConnected(has);
    }, 60000); // check every minute
    return () => clearInterval(check);
  }, []);

  const connectGoogle = async () => {
    try {
      const { getGoogleToken } = await import('../lib/google');
      await getGoogleToken();
      startAutoRefresh();
      setGoogleConnected(true);
      return true;
    } catch(e) {
      console.error('Google connect error:', e);
      return false;
    }
  };

  const syncCalendar = useCallback(async (projects) => {
    if (!getStoredToken()) return;
    try {
      setSyncing(true);
      const allEvents = [];

      // Sync each project's build calendar
      for (const project of (projects || [])) {
        if (!project.calendar_id) continue;
        try {
          const events = await fetchCalendarEvents(project.calendar_id, 14);
          for (const ev of events) {
            if (ev.status === 'cancelled') continue;
            const startDate = ev.start?.date || ev.start?.dateTime?.split('T')[0];
            const startTime = ev.start?.dateTime
              ? new Date(ev.start.dateTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
              : 'All Day';
            allEvents.push({
              project_id: project.id,
              google_event_id: ev.id,
              summary: ev.summary || '(No title)',
              event_date: startDate,
              event_time: startTime,
              calendar_name: project.name,
            });
          }
        } catch(e) { console.error(`Calendar sync error for ${project.name}:`, e); }
      }

      // Sync personal calendar
      try {
        const personalEvents = await fetchCalendarEvents('primary', 14);
        for (const ev of personalEvents) {
          if (ev.status === 'cancelled') continue;
          const startDate = ev.start?.date || ev.start?.dateTime?.split('T')[0];
          const startTime = ev.start?.dateTime
            ? new Date(ev.start.dateTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
            : 'All Day';
          allEvents.push({
            project_id: null,
            google_event_id: ev.id,
            summary: ev.summary || '(No title)',
            event_date: startDate,
            event_time: startTime,
            calendar_name: 'Personal',
          });
        }
      } catch(e) {}

      if (allEvents.length > 0) {
        await supabase.from('calendar_events').upsert(allEvents, { onConflict: 'google_event_id' });
      }
      setSyncing(false);
      return allEvents;
    } catch(e) {
      console.error('syncCalendar error:', e);
      setSyncing(false);
    }
  }, []);

  const syncDecisions = useCallback(async () => {}, []);

  return { googleConnected, syncing, connectGoogle, syncCalendar, syncDecisions };
}
